/**
 * Two-audio crossfade helper. Given the currently-playing `<audio>` element,
 * makes a detached clone that keeps playing while fading its volume from
 * `targetVolume` → 0 over `ms` ms, then pauses + nulls it. The main audio
 * element is expected to be reused by the caller with the NEXT track,
 * starting at 0 and ramped up over the same window.
 *
 * The fade uses a linear ramp on the DOM `<audio>.volume` — no Web Audio
 * graph, no gain node, no user-gesture unlock issues. Frame-driven with
 * `requestAnimationFrame` for smoothness. Aborts cleanly if the caller
 * bumps to yet another track mid-fade.
 */

export interface FadeHandle {
  cancel: () => void;
}

const FADE_INTERVAL_MS = 50;

export function fadeOutClone(source: HTMLAudioElement, targetVolume: number, ms: number): FadeHandle {
  const el = document.createElement("audio");
  el.crossOrigin = "anonymous";
  el.preload = "auto";
  el.src = source.src;
  el.currentTime = source.currentTime;
  el.volume = targetVolume;
  void el.play().catch(() => {});

  const start = performance.now();
  let raf = 0;
  let cancelled = false;

  const step = () => {
    if (cancelled) return;
    const t = (performance.now() - start) / ms;
    if (t >= 1) {
      el.volume = 0;
      try { el.pause(); } catch { /* ignore */ }
      el.src = "";
      el.remove();
      return;
    }
    el.volume = targetVolume * (1 - t);
    raf = window.setTimeout(step, FADE_INTERVAL_MS);
  };
  raf = window.setTimeout(step, FADE_INTERVAL_MS);

  return {
    cancel: () => {
      cancelled = true;
      window.clearTimeout(raf);
      try { el.pause(); } catch { /* ignore */ }
      el.src = "";
      el.remove();
    },
  };
}

export function fadeIn(el: HTMLAudioElement, targetVolume: number, ms: number): FadeHandle {
  el.volume = 0;
  const start = performance.now();
  let raf = 0;
  let cancelled = false;
  const step = () => {
    if (cancelled) return;
    const t = (performance.now() - start) / ms;
    if (t >= 1) { el.volume = targetVolume; return; }
    el.volume = targetVolume * t;
    raf = window.setTimeout(step, FADE_INTERVAL_MS);
  };
  raf = window.setTimeout(step, FADE_INTERVAL_MS);
  return {
    cancel: () => { cancelled = true; window.clearTimeout(raf); el.volume = targetVolume; },
  };
}
